import Big from 'big.js';
import {Subject, BehaviorSubject, combineLatest} from 'rxjs';
import {map, skipUntil, debounceTime, switchMap, filter, tap, switchMapTo} from 'rxjs/operators';
import {mc} from '@/core';
import {numUtil} from '@/helper/num';
import {fullImageUrlFun} from '@/helper/url';
import {strategyApi} from '@/api/strategy';
import {browser$} from '@/streams/browser';
import {isLogin$} from '@/streams/auth';
import {commonGray$} from '@/streams/user';
import {updateCustomize, contractBiz$} from '@/streams/contract-biz';
import {contractMarketsCache$} from '@/streams/market24h-contract';
import {contractsCache$} from '@/streams/config/contract';
import {spotsCache$} from '@/streams/config/spot';
import {botTradeAssets$} from '@/streams/accounts';
import {spotBotsTotalAssets$} from '@/streams/assets-spot';
import {botsUtil, calcUnrealisedPnl, createDisplayValue$} from '../utils';

const BIZ_KEY = 'strategy';

const traders$ = new BehaviorSubject(0);
const tradingEarnings$ = new BehaviorSubject(0);
const botsWorldwide$ = new BehaviorSubject(0);

const version$ = new BehaviorSubject(1);

export const spotBotsIsGray$ = new BehaviorSubject(true);
export const contractOpenOrders$ = new BehaviorSubject([]);
export const contractMartingaleOpenOrders$ = new BehaviorSubject([]);
export const contractSignalTradingOpenOrders$ = new BehaviorSubject([]);
export const spotOpenOrders$ = new BehaviorSubject([]);
export const botsCount$ = new BehaviorSubject(0);
export const displayTrader$ = createDisplayValue$(traders$);
export const displayTradingEarnings$ = createDisplayValue$(tradingEarnings$);
export const displayBotsWorldwide$ = createDisplayValue$(botsWorldwide$);
export const filterEnumRecord$ = new BehaviorSubject({
  directionList: [],
  pnlRatioList: [],
  suggestedPeriodList: [],
  symbolList: [],
});

export const assets$ = new BehaviorSubject<BotsAssets>(initAssets());

export const queryStrategyList$ = new Subject<StrategySquareQuery>();
export const loading$ = new BehaviorSubject(true);

const originalStrategySquareItems$ = new BehaviorSubject({page: 0, rows: [], totalCount: 1});
export const strategySquareItems$ = new BehaviorSubject({page: 0, rows: [], totalCount: 1});

export const customizeUpdate$ = new Subject();
export const strategySetting$ = new BehaviorSubject(initBiz());

export const isBotsHome$ = new BehaviorSubject(false);

browser$
  .pipe(
    switchMapTo(isBotsHome$),
    filter((isRequest) => isRequest),
    tap(() => getStrategySummer()),
    switchMapTo(isLogin$),
    filter((isLogin) => isLogin)
  )
  .subscribe(() => getMyOpenOrder());

queryStrategyList$.pipe(skipUntil(browser$)).subscribe((query) => Promise.all([getMarketplaceDropdownInfo(query), getMarketplaceSearch(query)]));

commonGray$.pipe(filter(({isInit}) => !isInit)).subscribe(({spotStrategyGrid}) => spotBotsIsGray$.next(spotStrategyGrid));

export const totalAssetsUsdt$ = botTradeAssets$.pipe(map((e) => numUtil.unAbbreviateNumber(e?.usdt || 0)));
export const totalSpotAssetsUsdt$ = spotBotsTotalAssets$.pipe(map((e) => numUtil.unAbbreviateNumber(e?.usdt || 0)));

const contractBotsAssets$ = combineLatest([contractMarketsCache$, contractsCache$, contractOpenOrders$, contractMartingaleOpenOrders$, contractSignalTradingOpenOrders$, totalAssetsUsdt$]).pipe(
  map(([contractMarketsCache, contractsCache, orders, martinOrders, signalOrders, totalAssets]) => {
    const items = [...orders, ...martinOrders, ...signalOrders].map((order) => {
      const contract = contractsCache.get(order.symbol);
      const market = contractMarketsCache.get(order.symbol);
      const item = calcUnrealisedPnl(market, order, contract);
      const unrealisedPnl = item.unrealisedPnl || 0;
      const todayPnl = unrealisedPnl !== 0 ? Big(unrealisedPnl).minus(order.preDayPnl) : order.preDayPnl;
      return {...order, todayPnl, unrealisedPnl};
    });

    const totalUnrealisedPnl = items.reduce((total, item) => total + Number(item.unrealisedPnl), 0);
    const totalRealisedPnl = items.reduce((total, item) => total + Number(item.realizedPnl), 0);
    const totalPreDayPnl = items.reduce((total, item) => total + Number(item.preDayPnl), 0);
    const todayPnl = totalRealisedPnl + totalUnrealisedPnl - totalPreDayPnl;
    return {totalAssets, todayPnl};
  })
);

const spotBotsAssets$ = combineLatest([spotOpenOrders$, totalSpotAssetsUsdt$, spotBotsIsGray$]).pipe(
  map(([orders, totalAssets, spotBotsIsGray]) => {
    if (!spotBotsIsGray) {
      return {totalAssets: 0, todayPnl: 0};
    }

    const items = orders.map((order) => {
      const {unRealizedPnl = 0, preDayPnl} = order;
      const todayPnl = unRealizedPnl !== 0 && unRealizedPnl && preDayPnl !== 0 ? Big(unRealizedPnl).minus(preDayPnl) : preDayPnl;
      return {...order, todayPnl};
    });
    const totalUnrealisedPnl = items.reduce((total, item) => total + Number(item.unRealizedPnl), 0);

    const totalRealisedPnl = items.reduce((total, item) => total + Number(item.realizedPnl || 0), 0);
    const totalPreDayPnl = items.reduce((total, item) => total + Number(item.preDayPnl), 0);
    const todayPnl = totalRealisedPnl + totalUnrealisedPnl - totalPreDayPnl;

    return {totalAssets, todayPnl};
  })
);

combineLatest([contractBotsAssets$, spotBotsAssets$])
  .pipe(map(addTotalAssets))
  .subscribe((o) => assets$.next(o));

function addTotalAssets(items) {
  const totalAssets = items.reduce((total, {totalAssets}) => total + totalAssets, 0);
  const todayPnl = items.reduce((total, {todayPnl}) => total + todayPnl, 0);
  const todayRoi = todayPnl === 0 || totalAssets === 0 ? 0 : Big(todayPnl).div(totalAssets);
  const totalAssetsStr = numUtil.floor(totalAssets, 4);
  const todayPnlStr = numUtil.floor(todayPnl, 4);
  const todayRoiStr = isNaN(todayRoi) ? '0%' : numUtil.floor(todayRoi, 2, -2) + '%';
  return {totalAssets, todayPnl, todayRoi, totalAssetsStr, todayPnlStr, todayRoiStr};
}

async function getStrategySummer() {
  const {data, error} = await strategyApi.marketplaceSummary();
  if (data) {
    const {strategyCnt, strategyEarning, strategyUserCnt} = data;
    traders$.next(strategyUserCnt);
    tradingEarnings$.next(strategyEarning);
    botsWorldwide$.next(strategyCnt);
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
}

contractBiz$.subscribe((data) => {
  const item = data.find((o) => o.bizKey === BIZ_KEY);
  if (item) {
    strategySetting$.next({...initBiz(), ...JSON.parse(item.bizVal)});
    version$.next(item.version);
  }
});

async function getMarketplaceDropdownInfo(query) {
  if (!query.strategyType) {
    return;
  }
  const {data, error} = await strategyApi.marketplaceDropdownInfo({strategyType: query.strategyType});
  if (data) {
    filterEnumRecord$.next(data);
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
}

async function getMarketplaceSearch(query) {
  if (!query.strategyType) {
    return;
  }
  loading$.next(true);
  const {data, error} = await strategyApi.marketplaceSearch(query);
  loading$.next(false);
  if (data) {
    originalStrategySquareItems$.next(data);
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
}

export async function getMyOpenOrder() {
  const {data} = await strategyApi.getOpenStrategy();
  if (data) {
    const {contractGrid, contractMartin, contractSignal, spotGrid} = data;
    contractOpenOrders$.next(contractGrid);
    contractMartingaleOpenOrders$.next(contractMartin);
    contractSignalTradingOpenOrders$.next(contractSignal);
    spotOpenOrders$.next(spotGrid);
    const orders = [...(contractGrid || []), ...(contractMartin || []), ...(spotGrid || []), ...(contractSignal || [])];
    botsCount$.next(orders.length ?? 0);
  }
}

function initAssets(): BotsAssets {
  return {
    totalAssets: 0,
    totalAssetsStr: '-',
    todayPnl: 0,
    todayPnlStr: '-',
    todayRoi: 0,
    todayRoiStr: '-%',
  };
}

function initBiz() {
  return {
    hideAsset: false,
  };
}

combineLatest([isLogin$, strategySetting$, customizeUpdate$])
  .pipe(
    debounceTime(1000),
    filter(([isLogin]) => isLogin),
    switchMap(([_, settings]) => {
      const curVersion = version$.getValue();
      return updateCustomize({bizKey: BIZ_KEY, curVersion, settings});
    })
  )
  .subscribe((version: number) => version$.next(version));

combineLatest([contractsCache$, spotsCache$, originalStrategySquareItems$])
  .pipe(
    map(([contractsCache, spotsCache, data]) => {
      const fullImageUrl = fullImageUrlFun();
      const rows = data?.rows?.map((item) => {
        const cache = ['CONTRACT_GRID', 'CONTRACT_MARTIN'].includes(botsUtil.curBotsType()) ? contractsCache : spotsCache;
        const cacheItem = cache.get(item.symbol);

        return {
          ...item,
          ownerAvatar: fullImageUrl(item.ownerAvatar),
          pnlRatioStr: numUtil.floor(Big(item.pnlRatio), 2, -2) + '%',
          totalAprStr: numUtil.floor(Big(item.totalApr), 2, -2) + '%',
          symbol: cacheItem ? cacheItem.baseCurrency + cacheItem.quoteCurrency : item.symbol,
          displaySymbol: cacheItem ? cacheItem.displaySymbol : item.symbol,
        };
      });
      return {...data, rows};
    })
  )
  .subscribe((items) => strategySquareItems$.next(items));
